import { Button, Form, Modal } from 'react-bootstrap';
import { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { hideLoadingModalReducer, showLoadingModalReducer } from 'src/store';
import { houseToHouseServices, hthHelper } from 'src/services';
import { Hr } from 'src/components/_commons';
import { hthConfigOptions } from 'src/app-config';
import { typeHTHBuilding, typeHTHHousehold, typeHTHFace, typeTerritoryNumber } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetIsDarkMode } from 'src/custom-hooks';

type propsType = {
    closeHTHModalHandler: () => void;
    currentFace: typeHTHFace;
    setShowComplex: Dispatch<SetStateAction<boolean>>;
    setStreetNumber: Dispatch<SetStateAction<number>>;
    setStreetNumber2: Dispatch<SetStateAction<number>>;
    setStreetNumber3: Dispatch<SetStateAction<number>>;
    streetNumber: number;
    streetNumber2: number;
    streetNumber3: number;
    territoryNumber: typeTerritoryNumber;
};

// common

export const HTHFaceBuildingsAddModalComplex: FC<propsType> = ({
    closeHTHModalHandler, currentFace, setShowComplex, setStreetNumber, setStreetNumber2,
    setStreetNumber3, streetNumber, streetNumber2, streetNumber3, territoryNumber
}) => {
    const [isSecondStage, setIsSecondStage] = useState(false);
    const [numberOfLevels, setNumberOfLevels] = useState(4);
    const [numberOfNumbers, setNumberOfNumbers] = useState(1);
    const [numberPerLevel, setNumberPerLevel] = useState(2);
    const [state, setState] = useState(hthHelper.GetNewComplexBuilding(numberOfLevels, numberPerLevel));
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();

    const submitHandler = async (event: ChangeEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        if (!isSecondStage) {
            setIsSecondStage(true);
            return;
        }
        const households: typeHTHHousehold[] = [];
        const x = [...document.getElementsByClassName('hthBuildingCheckboxInput')] as HTMLInputElement[];
        let areComplete = true;
        x.forEach(y => {
            const obj = JSON.parse(y.value) as { row: number; column: number; };
            const element = document.getElementById(`checkbox-${obj.column}-${obj.row}`);
            if (element?.classList.contains('bg-dark')) {
                const input = document.getElementById(`input-${obj.column}-${obj.row}`) as HTMLInputElement;
                if (input?.value) {
                    const doorName = input.value.trim();
                    if (doorName) {
                        households.push({
                            doorName: doorName.length > 20 ? doorName.substring(0, 20) : doorName,
                            doorNumber: obj.column,
                            history: [],
                            id: 0,
                            isChecked: false,
                            isCheckedNotAnswered: false,
                            isManager: false,
                            level: obj.row
                        });
                    } else {
                        areComplete = false;
                    }
                }
            }
        });
        if (!areComplete) {
            return alert("Completar todos los timbres");
        }
        if (!territoryNumber || !currentFace.street || !streetNumber || !households?.length) {
            alert("Faltan datos");
            return;
        }
        const building: typeHTHBuilding = {
            coords: null,
            dateOfLastMonthlySharing: null,
            dateOfLastSharing: null,
            hasCharacters: false,
            hasContinuousNumbers: false,
            hasLowLevel: false,
            households,
            id: 0,
            isComplex: true,
            numberOfLevels,
            numberPerLevel,
            reverseOrderX: false,
            reverseOrderY: false,
            streetNumber,
            streetNumber2: numberOfNumbers > 1 ? streetNumber2 : null,
            streetNumber3: numberOfNumbers > 2 ? streetNumber3 : null
        };
        dispatch(showLoadingModalReducer());
        const response = await houseToHouseServices.AddBuilding(territoryNumber, currentFace.block, currentFace.face, building);
        dispatch(hideLoadingModalReducer());
        if (!response.success) {
            alert(`${response.message.title} | ${response.message.description}`);  // keep alert (modal vs modal)
            return;
        }
        closeHTHModalHandler();
    }

    useEffect(() => {
        setState(hthHelper.GetNewComplexBuilding(numberOfLevels, numberPerLevel));
    }, [numberOfLevels, numberPerLevel]);

    return (
        <>
        <button className={'btn btn-general-red d-block mx-auto my-3'}
            style={{ width: '300px'}}
            onClick={() => setShowComplex(false)}
        >
            Cambiar a portero simple
        </button>
        <Hr classes={'my-4'} />
        <Form onSubmit={submitHandler}>
            <div className={'row d-flex align-self-center justify-content-center mt-2 mb-3'}>
                <Form.Group className={'fw-bolder m-2'}
                    style={{ width: '170px' }}
                >
                    <Form.Label>
                        Calle
                    </Form.Label>
                    <Form.Control type={'text'}
                        value={currentFace.street}
                        disabled
                    />
                    <Form.Label className={'mt-3'}>
                        Números
                    </Form.Label>
                    <Form.Select className={'text-center'}
                        value={numberOfNumbers.toString()}
                        onChange={e => setNumberOfNumbers(parseInt(e.target.value))}
                    >
                        <option value={1}>
                            1
                        </option>
                        <option value={2}>
                            2
                        </option>
                        <option value={3}>
                            3
                        </option>
                    </Form.Select>
                </Form.Group>
                <div className={'col-auto'}>
                    <Form.Group
                        className={'fw-bolder m-2'}
                        style={{ width: '120px' }}
                    >
                        <Form.Label>
                            Número
                        </Form.Label>
                        <Form.Control
                            type={'number'}
                            max={'100000'}
                            min={'1'}
                            value={streetNumber ? streetNumber : ''}
                            onChange={e => setStreetNumber(parseInt(e.target.value))}
                            autoFocus
                        />
                    </Form.Group>
                    {numberOfNumbers > 1 &&
                        <>
                        <Form.Group className={'fw-bolder m-2'}
                            style={{ width: '120px' }}
                        >
                            <Form.Label>
                                Número 2
                            </Form.Label>
                            <Form.Control type={'number'}
                                max={'100000'}
                                min={'1'}
                                value={streetNumber2 ? streetNumber2 : ''}
                                onChange={e => setStreetNumber2(parseInt(e.target.value))}
                            />
                        </Form.Group>
                        {numberOfNumbers > 2 &&
                            <Form.Group className={'fw-bolder m-2'}
                                style={{ width: '120px' }}
                            >
                                <Form.Label>
                                    Número 3
                                </Form.Label>
                                <Form.Control type={'number'}
                                    max={'100000'}
                                    min={'1'}
                                    value={streetNumber3 ? streetNumber3 : ''}
                                    onChange={e => setStreetNumber3(parseInt(e.target.value))}
                                />
                            </Form.Group>
                        }
                        </>
                    }
                </div>
                <Form.Group className={'fw-bolder m-2'}
                    style={{ width: '130px' }}
                >
                    <Form.Label>
                        Filas
                    </Form.Label>
                    <Form.Select className={'text-center'}
                        value={numberOfLevels.toString()}
                        onChange={e => isSecondStage ? null : setNumberOfLevels(parseInt(e.target.value))}
                        disabled={isSecondStage}
                    >
                        {hthConfigOptions.buildingLevels.map(level =>
                            <option key={level} value={level + 1}>
                                {level + 1}
                            </option>
                        )}
                    </Form.Select>
                </Form.Group>
                <Form.Group className={'fw-bolder m-2'}
                    style={{ width: '160px' }}
                >
                    <Form.Label>
                        Columnas
                    </Form.Label>
                    <Form.Select className={'text-center'}
                        style={{ width: '100px' }}
                        value={numberPerLevel.toString()}
                        onChange={e => isSecondStage ? null : setNumberPerLevel(parseInt(e.target.value))}
                        disabled={isSecondStage}
                    >
                        {hthConfigOptions.buildingDoorNumbers.map(doorNumber =>
                            <option key={doorNumber} value={doorNumber}>
                                {doorNumber}
                            </option>
                        )}
                    </Form.Select>
                </Form.Group>
            </div>
            <div className={`card my-4 ${streetNumber ? '' : 'd-none'}`}>
                <h1 className={'bg-dark text-white text-center fw-bolder mt-4 mb-2 py-2'}
                    style={{ border: isDarkMode ? '' : '1px solid lightgray', fontSize: '1.6rem' }}
                >
                    Esquema del Edificio:
                </h1>
                <Hr />
                <div className={'container'} style={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <div className={'d-flex'}>
                        {state.map((el, column) =>
                            <div key={column} className={'justify-content-center'}>
                                {el.map((item, row) =>
                                    <div key={`checkbox-${column}-${row}`}
                                        className={isSecondStage ? '' : 'pointer'}
                                        style={{ display: 'flex', justifyContent: 'space-around' }}
                                    >
                                        <div id={`checkbox-${column}-${row}`}
                                            className={'d-flex align-items-center bg-dark text-white text-center my-2'}
                                            style={{
                                                border: '1px solid black',
                                                borderRadius: '7px',
                                                marginInline: '10px',
                                                minHeight: '50px',
                                                width: '98px'
                                            }}
                                            onClick={() => {
                                                const thisButton = document.getElementById(`checkbox-${column}-${row}`);
                                                if (isSecondStage || !thisButton) {
                                                    return;
                                                }
                                                thisButton.classList.remove('bg-dark');
                                                thisButton.style.border = '1px solid white';
                                            }}
                                        >
                                            <input type={'hidden'}
                                                className={'hthBuildingCheckboxInput'}
                                                value={JSON.stringify({ column, row })}
                                            />
                                            {/* {level ? level : 'PB'} {String.fromCharCode(65 + door)} */}
                                            {isSecondStage && document.getElementById(`checkbox-${column}-${row}`)?.classList.contains('bg-dark') ?
                                                <input type='text'
                                                    id={`input-${column}-${row}`}
                                                    className={'form-control text-center'}
                                                />
                                                :
                                                item.label
                                            }
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Modal.Footer className={'justify-content-center align-items-center'}>
                <Button type={'submit'}
                    variant={'success'}
                    style={{ height: '40px', width: '120px' }}
                    disabled={!streetNumber}
                >
                    {isSecondStage ? 'ACEPTAR' : 'SIGUIENTE'}
                </Button>
                <Button variant={'secondary'}
                    style={{ height: '40px', width: '120px' }}
                    onClick={closeHTHModalHandler}
                >
                    CANCELAR
                </Button>
            </Modal.Footer>
        </Form>
        </>
    );
}
